import { Col, Row, Space, Typography } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useThemeComponents } from '../../../themes';
import useTranslatedString from '../../../utilities/useTranslatedString';
import OrderSummary from '../../portal/MobilityApplicationEntrypoint/Components/OrderSummary';
import {
    type MobilityApplicationStateWithExpired,
    useMobilityBookingLocation,
} from '../../portal/MobilityApplicationEntrypoint/helper';
import BookingDeposit from './BookingDeposit';
import SubmittedContent from './SubmittedContent';
import VehicleDetails from './VehicleDetails';

const colSpan = { lg: 8, md: 12, sm: 24, xs: 24 };

export type MobilityContentProps<T> = {
    subTitle: string;
    description: string;
    reference: string;
    application: T;
    externalModelInfoBaseUrl?: string;
};
const MobilityContent = <T extends MobilityApplicationStateWithExpired>({
    application,
    subTitle,
    description,
    reference,
    externalModelInfoBaseUrl,
}: MobilityContentProps<T>) => {
    const navigate = useNavigate();
    const { t } = useTranslation(['mobilityThankYou', 'mobilityOrderSummary', 'paymentDetails']);
    const { Button } = useThemeComponents();

    const locationInfo = useMobilityBookingLocation(application);

    const translate = useTranslatedString();

    const onClickAddress = useCallback(() => {
        window.open(`https://${locationInfo.url}`, '_blank');
    }, [locationInfo]);

    const onClickExplore = useCallback(() => {
        if (application.vehicle.__typename === 'LocalVariant' && externalModelInfoBaseUrl) {
            const { identifier } = application.vehicle.model;

            globalThis.open(
                `https://${externalModelInfoBaseUrl}/middle-east/models/${identifier}/${identifier}-models/`,
                '_blank'
            );
        }
    }, [application, externalModelInfoBaseUrl]);

    const hasDeposit = !!application.deposit;

    return (
        <Row gutter={[24, 24]} justify="center" style={{ marginBottom: '24px' }}>
            <Col {...colSpan}>
                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                    <VehicleDetails
                        vehicleSection={
                            <OrderSummary
                                application={application}
                                showGiftVoucher={!!application.giftVoucher}
                                showPromoCode={!!application.promoCode}
                            />
                        }
                    />
                    {hasDeposit && <BookingDeposit depositAmount={application.deposit.amount} />}
                </Space>
            </Col>
            <Col {...colSpan}>
                <Space direction="vertical" size={32} style={{ width: '100%' }}>
                    <SubmittedContent description={description} reference={reference} title={subTitle} />
                    <Space direction="vertical" size={24} style={{ width: '100%' }}>
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            {application.mobilityBookingDetails.location.__typename ===
                                'MobilityBookingLocationPickup' && (
                                <>
                                    <Typography.Title level={4} style={{ marginBottom: 0 }}>
                                        {locationInfo.name}
                                    </Typography.Title>
                                    <div>
                                        <Typography.Link
                                            onClick={onClickAddress}
                                            style={{ display: 'block', fontSize: '16px' }}
                                            underline
                                        >
                                            {locationInfo.address}
                                        </Typography.Link>
                                        <Typography.Text style={{ display: 'block', fontSize: '16px' }}>
                                            {t('mobilityOrderSummary:locationInfo.phone', {
                                                value: locationInfo.phone,
                                            })}
                                        </Typography.Text>
                                        <Typography.Text style={{ display: 'block', fontSize: '16px' }}>
                                            {t('mobilityOrderSummary:locationInfo.email', {
                                                email: locationInfo.email,
                                            })}
                                        </Typography.Text>
                                    </div>
                                </>
                            )}
                            {application.mobilityBookingDetails.location.__typename === 'MobilityBookingLocationHome' &&
                                application.dealer && (
                                    <>
                                        <Typography.Title level={4} style={{ marginBottom: 0 }}>
                                            {translate(application.dealer.legalName)}
                                        </Typography.Title>
                                        <div>
                                            {application.dealer.contact.address?.defaultValue && (
                                                <Typography.Link
                                                    onClick={onClickAddress}
                                                    style={{ display: 'block', fontSize: '16px' }}
                                                    underline
                                                >
                                                    {translate(application.dealer.contact.address)}
                                                </Typography.Link>
                                            )}
                                            {application.dealer.contact.telephone && (
                                                <Typography.Text style={{ display: 'block', fontSize: '16px' }}>
                                                    {t('mobilityOrderSummary:locationInfo.phone', {
                                                        value: [
                                                            application.dealer.contact.telephone?.prefix
                                                                ? `+${application.dealer.contact.telephone?.prefix}`
                                                                : '',
                                                            application.dealer.contact.telephone?.value ?? '',
                                                        ].join(' '),
                                                    })}
                                                </Typography.Text>
                                            )}

                                            {application.dealer.contact.email && (
                                                <Typography.Text style={{ display: 'block', fontSize: '16px' }}>
                                                    {t('mobilityOrderSummary:locationInfo.email', {
                                                        email: application.dealer.contact.email,
                                                    })}
                                                </Typography.Text>
                                            )}
                                        </div>
                                    </>
                                )}
                        </Space>
                        <Space direction="vertical" size={16} style={{ width: '100%' }}>
                            <Button onClick={() => navigate('..')} type="primary" block>
                                {t('mobilityThankYou:actions.continueBrowsing')}
                            </Button>
                            {externalModelInfoBaseUrl && (
                                <Button onClick={onClickExplore} type="tertiary" block>
                                    {t('mobilityThankYou:actions.moreDetails')}
                                </Button>
                            )}
                        </Space>
                    </Space>
                </Space>
            </Col>
        </Row>
    );
};

export default MobilityContent;
